/**
 * Every path this bundle knows, in one file.
 *
 * The screens never build a URL. They call one of these, which means a route
 * renamed on the Worker breaks exactly one file and a compile, rather than
 * failing silently at runtime on a station tablet. The shapes here are the
 * response contracts of apps/api/src/ops/routes.ts, narrowed to what a screen
 * actually renders - a screen that cannot name a field cannot leak it.
 */
import { api } from '../api'

export interface PublicListing {
  id: string
  name: string
  captionMd: string | null
  pricePaise: number
  qtyAvailable: number
  soldOut: boolean
  photoR2Key: string | null
  ageGroupId: string | null
  categoryId: string | null
  createdAt: string
}

export interface BrowsePage {
  items: PublicListing[]
  nextCursor: string | null
}

/** Search answers the same projection browse does, plus what it matched and how many. */
export interface SearchPage extends BrowsePage {
  matched: number
  q: string
}

export interface HomeQueueItem {
  id: string
  noun: string
  label: string
  verb: string
  count: number
  path: string
  weight: number
  attention: boolean
}

export interface HomeQueueResponse {
  items: HomeQueueItem[]
  overflow: HomeQueueItem[]
  allClear: boolean
  headline: HomeQueueItem | null
  role: string
  countsReadAt: string
}

export interface SupplierPayment {
  id: string
  supplierName: string | null
  amountPaise: number
  status: string
  dueDate: string | null
  method: string
}

export interface InboundConsignment {
  id: string
  supplierName: string | null
  status: string
  expectedDate: string | null
  transporter: string | null
  declaredAmountPaise: number | null
}

export interface CartLine {
  id: string
  unitId: string
  titleName?: string
  unitPricePaise: number
  qty: number
  lineTotalPaise: number
}

export interface CartView {
  cartId: string
  customerId: string
  status: string
  lines: CartLine[]
  subtotalPaise: number
  shippingPaise: number
  creditAppliedPaise: number
  payablePaise: number
}

export interface OrderView {
  id: string
  orderNo: string
  status: string
  totalPaise: number
  placedAt?: string
}

export interface CreditBalance {
  customerId: string
  balancePaise: number
  entryCount: number
}

export interface CreditEntry {
  direction: 'credit' | 'debit'
  amountPaise: number
  reason?: string
  createdAt?: string
}

export interface PickListResponse {
  lines: Array<{ unitId: string; titleName?: string; title?: string; binLabel: string; walkSequence: number }>
}

export interface ConsolidationResponse {
  groups: Array<{ orderId: string; orderNo?: string; units: Array<{ unitId: string; titleName?: string; title?: string }> }>
}

export interface CatalogueQueueResponse {
  items: Array<{
    unitId: string
    titleName?: string
    title?: string
    format?: string
    ageGroup?: string
    grade?: 'A' | 'B' | 'C'
    pricePaise?: number
    defects?: string[]
  }>
}

export interface ContestedClaimsResponse {
  items: Array<{
    dropItemId: string
    titleName?: string
    title?: string
    claims: Array<{ claimId: string; claimantName?: string; claimant?: string; groupName?: string; group?: string; claimedAt: string; rank: number }>
  }>
}

export interface RolloutResponse {
  people: Array<{ userId: string; name: string; role: string; features: string[] }>
}

export interface MeFeatures {
  userId: string
  role: string
  features: string[]
}

/** Path builders, exported so a test can prove them without a network. */
export const paths = {
  storefrontListings: (q: { ageGroupId?: string | null; categoryId?: string | null; cursor?: string | null } = {}) => {
    const qs = new URLSearchParams()
    if (q.ageGroupId) qs.set('ageGroupId', q.ageGroupId)
    if (q.categoryId) qs.set('categoryId', q.categoryId)
    if (q.cursor) qs.set('cursor', q.cursor)
    const s = qs.toString()
    return `/storefront/listings${s ? `?${s}` : ''}`
  },
  /**
   * Search. `q` is not optional here: a search path built without a query is a
   * request for the whole catalogue, and that is the bug that makes a search box
   * feel broken the first time someone taps it with an empty field.
   */
  storefrontSearch: (q: string, opts: { cursor?: string | null; ageGroupId?: string | null; categoryId?: string | null } = {}) => {
    const qs = new URLSearchParams({ q })
    if (opts.cursor) qs.set('cursor', opts.cursor)
    if (opts.ageGroupId) qs.set('ageGroupId', opts.ageGroupId)
    if (opts.categoryId) qs.set('categoryId', opts.categoryId)
    return `/storefront/search?${qs.toString()}`
  },
  homeQueue: () => '/home-queue',
  supplierPayments: () => '/supplier-payments',
  inboundConsignments: () => '/inbound-consignments',
  storefrontListing: (id: string) => `/storefront/listings/${encodeURIComponent(id)}`,
  magicLink: () => '/storefront/magic-link',
  storefrontClaim: () => '/storefront/claim',
  storefrontCartItems: () => '/storefront/cart-items',
  cart: (id: string) => `/carts/${encodeURIComponent(id)}`,
  cartConvert: (id: string) => `/carts/${encodeURIComponent(id)}/convert`,
  order: (id: string) => `/orders/${encodeURIComponent(id)}`,
  creditBalance: (customerId: string) => `/customers/${encodeURIComponent(customerId)}/credit-balance`,
  creditEntries: (customerId: string) => `/customers/${encodeURIComponent(customerId)}/credit-entries`,
  pickList: (roundId: string) => `/pick-rounds/${encodeURIComponent(roundId)}/pick-list`,
  consolidation: (roundId: string) => `/pick-rounds/${encodeURIComponent(roundId)}/consolidation`,
  pickScan: (roundId: string) => `/pick-rounds/${encodeURIComponent(roundId)}/scan`,
  packScan: (parcelId: string) => `/parcels/${encodeURIComponent(parcelId)}/pack-scan`,
  closeParcel: (parcelId: string) => `/parcels/${encodeURIComponent(parcelId)}/close`,
  catalogueQueue: () => '/catalogue/queue',
  catalogueStart: () => '/catalogue/start',
  catalogueComplete: () => '/catalogue/complete',
  contestedClaims: () => '/claims/contested',
  adjudicate: (dropItemId: string) => `/claims/contests/${encodeURIComponent(dropItemId)}/adjudicate`,
  manualEntryQueue: () => '/manual-entry-queue',
  outboxAssisted: () => '/outbox/assisted',
  shipmentRetryQueue: () => '/shipments/retry-queue',
  rollout: () => '/admin/rollout',
  meFeatures: () => '/me/features',
} as const

export const endpoints = {
  browse: (q: Parameters<typeof paths.storefrontListings>[0] = {}) => api<BrowsePage>(paths.storefrontListings(q)),
  listing: (id: string) => api<PublicListing>(paths.storefrontListing(id)),
  search: (q: string, opts: Parameters<typeof paths.storefrontSearch>[1] = {}) =>
    api<SearchPage>(paths.storefrontSearch(q, opts)),
  homeQueue: () => api<HomeQueueResponse>(paths.homeQueue()),
  supplierPayments: () => api<{ items: SupplierPayment[] }>(paths.supplierPayments()),
  inboundConsignments: () => api<{ items: InboundConsignment[] }>(paths.inboundConsignments()),
  requestMagicLink: (phone: string) =>
    api<{ ok: boolean; reason?: string; retryAfterMs?: number; reissued?: boolean }>(paths.magicLink(), {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),
  /**
   * The key is a parameter, not something this function mints, because a retry of
   * one tap must carry the key of that tap. See storefront/addToCart.ts: a key
   * generated in here would be a fresh key per fetch, which is a second addition.
   *
   * It travels as a header rather than in the body so that the retried request is
   * byte-identical to the first - a client that had both could change one and not
   * the other, and the server would answer IDEMPOTENCY_KEY_REUSED.
   */
  addToCart: (listingId: string, qty: number, idempotencyKey: string) =>
    api<{ accepted: boolean; replayed: boolean; cartId: string; cartItemId: string }>(paths.storefrontCartItems(), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ listingId, qty }),
    }),
  cart: (id: string) => api<CartView>(paths.cart(id)),
  order: (id: string) => api<{ order: OrderView }>(paths.order(id)),
  creditBalance: (customerId: string) => api<CreditBalance>(paths.creditBalance(customerId)),
  creditEntries: (customerId: string) => api<{ customerId: string; items: CreditEntry[] }>(paths.creditEntries(customerId)),
  pickList: (roundId: string) => api<PickListResponse>(paths.pickList(roundId)),
  consolidation: (roundId: string) => api<ConsolidationResponse>(paths.consolidation(roundId)),
  catalogueQueue: () => api<CatalogueQueueResponse>(paths.catalogueQueue()),
  contestedClaims: () => api<ContestedClaimsResponse>(paths.contestedClaims()),
  rollout: () => api<RolloutResponse>(paths.rollout()),
  meFeatures: () => api<MeFeatures>(paths.meFeatures()),
} as const
